import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Wrench,
  FolderKanban,
  Tags,
  Code2,
  MessageSquareQuote,
  BadgePercent,
  Inbox,
  Settings as SettingsIcon,
  LogOut,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ASSETS } from "../../lib/site";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/services", label: "Servicios", icon: Wrench },
  { to: "/admin/brands", label: "Marcas", icon: Tags },
  { to: "/admin/software", label: "Software GLG", icon: Code2 },
  { to: "/admin/testimonials", label: "Testimonios", icon: MessageSquareQuote },
  { to: "/admin/promotions", label: "Promociones", icon: BadgePercent },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/settings", label: "Configuración", icon: SettingsIcon, adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-6 py-6">
        <img src={ASSETS.logo} alt="GLG" className="h-8 w-auto" />
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-glg-blue text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="space-y-1 border-t border-white/10 p-3">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white">
          <ExternalLink className="h-4 w-4" /> Ver sitio
        </a>
        <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white">
          <LogOut className="h-4 w-4" /> Cerrar sesión
        </button>
        <div className="px-4 pt-2 text-xs text-white/30">{user?.email}</div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-glg-black text-white">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-white/10 bg-glg-surface lg:flex">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col border-r border-white/10 bg-glg-surface">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-white/10 bg-glg-surface/60 px-5 py-3 backdrop-blur lg:hidden">
          <img src={ASSETS.logo} alt="GLG" className="h-7 w-auto" />
          <button onClick={() => setOpen(true)} data-testid="admin-menu-toggle" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15">
            <Menu className="h-5 w-5" />
          </button>
        </header>
        <main className="flex-1 overflow-y-auto p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
